import { useEffect, useState, useCallback } from 'react';
import { Share2, Facebook, Instagram, CheckCircle2, AlertTriangle, ExternalLink, Send, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from '@/hooks/useAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inp = "w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 focus:border-indigo-500 outline-none";

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs text-zinc-400 block mb-1">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-zinc-600 mt-0.5 block">{hint}</span>}
    </label>
  );
}

export default function AdminSocial() {
  const { authFetch } = useAdmin();
  const [creds, setCreds] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verify, setVerify] = useState(null);
  const [log, setLog] = useState([]);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/admin/social/credentials`);
      if (r.ok) setCreds(await r.json());
    } catch (e) { console.error(e); }
    try {
      const r2 = await authFetch(`${API}/admin/social/log?limit=50`);
      if (r2.ok) setLog((await r2.json()).entries || []);
    } catch (e) { console.error(e); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  function set(k, v) { setCreds(c => ({ ...c, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        fb_enabled: !!creds.fb_enabled,
        ig_enabled: !!creds.ig_enabled,
        tiktok_enabled: !!creds.tiktok_enabled,
        twitter_enabled: !!creds.twitter_enabled,
        fb_page_id: creds.fb_page_id || '',
        ig_business_account_id: creds.ig_business_account_id || '',
        site_base_url: creds.site_base_url || '',
      };
      // Only send token if admin typed a new one (the input is a separate
      // state tracked via `creds._new_fb_token`)
      if (creds._new_fb_token) payload.fb_page_access_token = creds._new_fb_token;
      if (creds._new_tiktok_token) payload.tiktok_access_token = creds._new_tiktok_token;
      if (creds._new_twitter_token) payload.twitter_bearer_token = creds._new_twitter_token;
      const r = await authFetch(`${API}/admin/social/credentials`, {
        method: 'PUT', body: JSON.stringify(payload),
      });
      if (r.ok) { toast.success('Postavke spremljene'); await load(); }
      else toast.error('Spremanje nije uspjelo');
    } catch (e) { toast.error(String(e)); }
    finally { setSaving(false); }
  }

  async function verifyTokens() {
    setVerify({ loading: true });
    try {
      const r = await authFetch(`${API}/admin/social/verify`, { method: 'POST' });
      const d = await r.json();
      setVerify(d);
      if (d.fb?.ok) toast.success(`FB token OK — stranica: ${d.fb.page_name}`);
      else toast.error(`FB: ${d.fb?.error || 'nevažeći'}`);
    } catch (e) { toast.error(String(e)); setVerify(null); }
  }

  async function runScanNow() {
    setPosting(true);
    try {
      const r = await authFetch(`${API}/admin/social/scan-and-post?batch_limit=5`, { method: 'POST' });
      const d = await r.json();
      toast.success(`Objavljeno: ${d.picked || 0} članaka`);
      await load();
    } catch (e) { toast.error(String(e)); }
    finally { setPosting(false); }
  }

  if (!creds) return <div className="p-6 text-zinc-500">Učitavanje…</div>;

  return (
    <div className="p-6 max-w-5xl" data-testid="admin-social">
      <div className="flex items-center gap-3 mb-6">
        <Share2 className="text-indigo-400" size={24} />
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100">Auto-objava na društvene mreže</h1>
          <p className="text-sm text-zinc-500">AI-generirane objave automatski idu na Facebook i Instagram kad objaviš članak.</p>
        </div>
      </div>

      {/* Facebook */}
      <section className="border border-zinc-800 rounded-lg p-4 mb-4 bg-zinc-950/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-blue-400">
            <Facebook size={18} /> <h2 className="font-semibold">Facebook Page</h2>
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={!!creds.fb_enabled}
                   onChange={e => set('fb_enabled', e.target.checked)}
                   data-testid="fb-enabled-toggle" />
            Aktivno
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Facebook Page ID" hint="Broj stranice (Settings → About → Page ID)">
            <input className={inp} value={creds.fb_page_id || ''}
                   onChange={e => set('fb_page_id', e.target.value)}
                   data-testid="fb-page-id" />
          </Field>
          <Field label="Page Access Token (long-lived)"
                 hint={creds.has_fb_token ? `Trenutno spremljen: ${creds.fb_page_access_token_masked}. Ostavi prazno da zadržiš.` : 'Nije postavljen.'}>
            <input className={inp} type="password" placeholder="EAAG…"
                   onChange={e => set('_new_fb_token', e.target.value)}
                   data-testid="fb-token" />
          </Field>
        </div>
        <div className="text-[11px] text-zinc-500 mt-3 bg-zinc-900/60 border border-zinc-800 rounded p-2">
          Kako dobiti trajni token: developers.facebook.com → My Apps → Graph API Explorer → generiraj user token s permissionima
          <code className="bg-zinc-800 px-1 mx-1">pages_manage_posts</code>
          <code className="bg-zinc-800 px-1">pages_read_engagement</code>
          <code className="bg-zinc-800 px-1 mx-1">instagram_basic</code>
          <code className="bg-zinc-800 px-1">instagram_content_publish</code>
          , zamijeni ga za long-lived token pa za Page token. <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">Graph API Explorer <ExternalLink size={10} className="inline"/></a>
        </div>
      </section>

      {/* Instagram */}
      <section className="border border-zinc-800 rounded-lg p-4 mb-4 bg-zinc-950/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-pink-400">
            <Instagram size={18} /> <h2 className="font-semibold">Instagram Business</h2>
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={!!creds.ig_enabled}
                   onChange={e => set('ig_enabled', e.target.checked)}
                   data-testid="ig-enabled-toggle" />
            Aktivno
          </label>
        </div>
        <Field label="Instagram Business Account ID"
               hint="IG mora biti tip 'Business' ili 'Creator' i povezan s iznad navedenom FB stranicom. Dijeli isti Page Access Token.">
          <input className={inp} value={creds.ig_business_account_id || ''}
                 onChange={e => set('ig_business_account_id', e.target.value)}
                 data-testid="ig-account-id" />
        </Field>
        <p className="text-[11px] text-zinc-500 mt-2">
          Napomena: IG zahtijeva <strong>javno dostupni</strong> <code>cover_image_url</code> — naše <code>/api/uploads/</code> slike rade jer se preview domain serva preko HTTPS-a.
        </p>
      </section>

      {/* TikTok + X (prepared, disabled) */}
      <section className="border border-zinc-800 rounded-lg p-4 mb-4 bg-zinc-950/30 opacity-60">
        <h2 className="font-semibold text-zinc-400 mb-2">TikTok & X (uskoro)</h2>
        <p className="text-xs text-zinc-500">Infrastruktura je spremna, čeka se odobrenje TikTok Business partnerstva i X API v2 pristupa.</p>
      </section>

      {/* Site base URL */}
      <Field label="Bazni URL za linkove u objavama">
        <input className={inp} value={creds.site_base_url || ''}
               onChange={e => set('site_base_url', e.target.value)}
               data-testid="site-base-url" />
      </Field>

      {/* Actions */}
      <div className="flex gap-2 mt-6 flex-wrap">
        <button onClick={save} disabled={saving}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-md text-sm font-semibold flex items-center gap-1.5"
                data-testid="save-social-creds-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : null} Spremi postavke
        </button>
        <button onClick={verifyTokens}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-md text-sm flex items-center gap-1.5"
                data-testid="verify-tokens-btn">
          <RefreshCw size={14} /> Provjeri tokene
        </button>
        <button onClick={runScanNow} disabled={posting}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-md text-sm font-semibold flex items-center gap-1.5"
                data-testid="run-scan-btn">
          {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Pokreni scan sada
        </button>
      </div>

      {verify && !verify.loading && (
        <div className="mt-4 grid md:grid-cols-2 gap-2">
          <div className={`p-3 rounded border ${verify.fb?.ok ? 'border-emerald-800 bg-emerald-950/30 text-emerald-300' : 'border-red-800 bg-red-950/30 text-red-300'}`}>
            {verify.fb?.ok ? <CheckCircle2 size={14} className="inline" /> : <AlertTriangle size={14} className="inline" />}
            <strong className="ml-1">Facebook:</strong> {verify.fb?.ok ? `OK — ${verify.fb.page_name}` : verify.fb?.error}
          </div>
          <div className={`p-3 rounded border ${verify.ig?.ok ? 'border-emerald-800 bg-emerald-950/30 text-emerald-300' : 'border-red-800 bg-red-950/30 text-red-300'}`}>
            {verify.ig?.ok ? <CheckCircle2 size={14} className="inline" /> : <AlertTriangle size={14} className="inline" />}
            <strong className="ml-1">Instagram:</strong> {verify.ig?.ok ? `OK — linked ID: ${verify.ig.linked_id}` : verify.ig?.error}
          </div>
        </div>
      )}

      {/* Recent log */}
      <section className="mt-8">
        <h2 className="text-lg text-zinc-200 mb-3 flex items-center gap-2">
          <Share2 size={16} /> Zadnjih 50 objava
        </h2>
        {log.length === 0 ? (
          <p className="text-sm text-zinc-500">Još nije bilo automatskih objava.</p>
        ) : (
          <div className="overflow-x-auto border border-zinc-800 rounded">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900 text-zinc-400">
                <tr>
                  <th className="text-left px-3 py-2">Vrijeme</th>
                  <th className="text-left px-3 py-2">Mreža</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Članak</th>
                  <th className="text-left px-3 py-2">Greška</th>
                </tr>
              </thead>
              <tbody>
                {log.map((e, i) => (
                  <tr key={i} className="border-t border-zinc-800 text-zinc-300">
                    <td className="px-3 py-2 font-mono">{(e.posted_at || '').slice(0,19).replace('T',' ')}</td>
                    <td className="px-3 py-2">{e.platform}</td>
                    <td className="px-3 py-2">
                      <span className={e.status === 'success' ? 'text-emerald-400' : e.status === 'failed' ? 'text-red-400' : 'text-zinc-500'}>
                        {e.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px]">{(e.article_id || '').slice(0,8)}</td>
                    <td className="px-3 py-2 text-red-300 max-w-xs truncate">{e.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
